<template>
  <div class="page-wrap">

    <!-- ── Stat Cards ─────────────────────────── -->
    <v-row dense>
      <v-col cols="6" sm="3">
        <PremiumStatCard
          label="Total Polls"
          :value="stats.total"
          icon="mdi-poll"
          color="#3B82F6"
          :loading="loading"
        />
      </v-col>
      <v-col cols="6" sm="3">
        <PremiumStatCard
          label="Active"
          :value="stats.active"
          icon="mdi-check-circle-outline"
          color="#10B981"
          :loading="loading"
        />
      </v-col>
      <v-col cols="6" sm="3">
        <PremiumStatCard
          label="Draft"
          :value="stats.draft"
          icon="mdi-pencil-box-outline"
          color="#94A3B8"
          :loading="loading"
        />
      </v-col>
      <v-col cols="6" sm="3">
        <PremiumStatCard
          label="Expired"
          :value="stats.expired"
          icon="mdi-clock-alert-outline"
          color="#F59E0B"
          :loading="loading"
        />
      </v-col>
    </v-row>

    <PremiumTable
      title="Polls"
      subtitle="Manage community polls and view live results"
      add-label="Create Poll"
      :show-view="true"
      :show-edit="true"
      :show-delete="true"
      @add="$router.push('/societyadmin/poll/add')"
      @view="$router.push('/societyadmin/poll/view/' + $event.id)"
      @edit="onEditPoll"
      @delete="confirmDeleteDialog($event.id)"
      :columns="columns"
      :items="polls"
      :loading="loading"
      :total-items="totalRecords"
      :page.sync="page"
      :items-per-page.sync="itemsPerPage"
      :searchable="true"
      :search="search"
      @search="onSearch"
      @update:options="onOptionsUpdate"
    >
      <template #header-extra>
        <FilterDropdown
          v-model="statusFilter"
          :options="statusOptions"
          item-text="label"
          item-value="value"
          placeholder="All Status"
          all-label="All Status"
          :min-width="160"
          @change="onFilterChange"
        />
      </template>

      <!-- Custom Cells -->
      <template #col-title="{ item }">
        <span class="cell-name">{{ item.title }}</span>
      </template>

      <template #col-type="{ item }">
        <div class="d-flex align-center">
          <v-icon size="14" class="mr-1" color="#94A3B8">{{ getTypeIcon(item.type) }}</v-icon>
          <span style="font-size: 13px; color: #475569;">{{ getTypeLabel(item.type) }}</span>
        </div>
      </template>

      <template #col-status="{ item }">
        <div
          class="status-pill"
          :style="getStatusStyles(item.status)"
        >
          <span class="status-dot" :style="{ background: getStatusColor(item.status) }"></span>
          <span>{{ getStatusLabel(item.status) }}</span>
          <!-- <v-icon size="12" :color="getStatusColor(item.status)" class="ml-1">mdi-chevron-down</v-icon> -->
        </div>
      </template>

      <template #col-start_date="{ item }">
        <span class="date-text">{{ formatDate(item.start_date) }}</span>
      </template>

      <template #col-expiry_date="{ item }">
        <span class="date-text">{{ formatDate(item.expiry_date) }}</span>
      </template>

      <template #actions="{ item }">
        <ActionButtons
          :show-view="true"
          :show-edit="item.status !== 'expired' && !(item.status === 'active' && new Date() >= new Date(item.start_date))"
          :show-delete="true"
          @view="$router.push('/societyadmin/poll/view/' + item.id)"
          @edit="onEditPoll(item)"
          @delete="confirmDeleteDialog(item.id)"
        />
      </template>
    </PremiumTable>

    <!-- Delete Confirmation -->
    <ConfirmModal
      v-model="deleteDialog"
      type="danger"
      title="Delete Poll"
      message="Are you sure you want to delete this poll? This action cannot be undone."
      confirm-text="Yes, Delete"
      cancel-text="Cancel"
      :loading="deleteLoading"
      @confirm="deletePoll"
    />

    <!-- Status Change Menu -->
    <v-menu
      v-model="statusMenu.show"
      :position-x="statusMenu.x"
      :position-y="statusMenu.y"
      absolute
      offset-y
      rounded="lg"
      min-width="160"
      content-class="elevation-2"
    >
      <v-list class="py-1" dense>
        <v-list-item
          v-for="status in getAvailableStatuses(statusMenu.item)"
          :key="status.value"
          @click="updateStatus(status.value)"
          :disabled="statusMenu.item && statusMenu.item.status === status.value"
          class="px-3"
        >
          <div class="d-flex align-center" style="gap: 8px">
            <span class="status-dot" :style="{ background: getStatusColor(status.value) }"></span>
            <span style="font-size: 13px; font-weight: 500; color: #334155">{{ status.label }}</span>
          </div>
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import PremiumTable from "../../common/PremiumTable.vue";
import PremiumStatCard from "../../common/PremiumStatCard.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import ActionButtons from "../../common/ActionButtons.vue";
import FilterDropdown from "../../common/FilterDropdown.vue";

export default {
  name: "ListPoll",
  components: { PremiumTable, PremiumStatCard, ConfirmModal, ActionButtons, FilterDropdown },

  data() {
    return {
      loading: false,
      polls: [],
      totalRecords: 0,
      page: 1,
      itemsPerPage: 10,
      search: "",
      statusFilter: "",
      statusOptions: [
        { label: "Active", value: "active" },
        { label: "Expired", value: "expired" },
        { label: "Draft", value: "draft" },
      ],
      deleteDialog: false,
      deleteLoading: false,
      pollToDelete: null,
      statusMenu: { show: false, x: 0, y: 0, item: null },
      columns: [
        { label: "Title",       key: "title",       width: "30%" },
        { label: "Type",        key: "type",        width: "15%" },
        { label: "Status",      key: "status",      width: "15%" },
        { label: "Start Date",  key: "start_date",  width: "15%" },
        { label: "Expiry Date", key: "expiry_date", width: "15%" },
      ],
    };
  },

  computed: {
    ...mapState("theme", ["primaryColor"]),
    stats() {
      return {
        total:   this.totalRecords,
        active:  this.polls.filter(p => p.status === "active").length,
        draft:   this.polls.filter(p => p.status === "draft").length,
        expired: this.polls.filter(p => p.status === "expired").length,
      };
    },
  },

  watch: {
    page()         { this.fetchPolls(); },
    itemsPerPage() { this.fetchPolls(); },
  },

  mounted() {
    this.fetchPolls();
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    onSearch(val) {
      this.search = val;
      this.page = 1;
      this.fetchPolls();
    },

    onOptionsUpdate({ page, itemsPerPage }) {
      this.page = page;
      this.itemsPerPage = itemsPerPage;
      this.fetchPolls();
    },

    onFilterChange() {
      this.page = 1;
      this.fetchPolls();
    },

    async fetchPolls() {
      this.loading = true;
      try {
        const response = await this.$axios.get("/societyadmin/polls", {
          params: { 
            page: this.page, 
            perPage: this.itemsPerPage, 
            search: this.search,
            status: this.statusFilter || ''
          },
        });
        if (response.data.status) {
          this.polls = response.data.data;
          this.totalRecords = response.data.pagination.total;
        }
      } catch (error) {
        this.showError("Failed to load polls");
      } finally {
        this.loading = false;
      }
    },

    onEditPoll(item) {
      // if (item.status === "active" || item.status === "expired") {
      //   this.showError("Cannot edit a poll once it's active or expired.");
      //   return;
      // }
      this.$router.push("/societyadmin/poll/edit/" + item.id);
    },

    confirmDeleteDialog(id) {
      this.pollToDelete = id;
      this.deleteDialog = true;
    },

    async deletePoll() {
      this.deleteLoading = true;
      try {
        const res = await this.$axios.delete(`/societyadmin/polls/${this.pollToDelete}`);
        if (res.data.status) {
          this.deleteDialog = false;
          this.fetchPolls();
          this.setAlert({ showAlert: true, message: res.data.message, variant: "green accent-4", timeout: 2000 });
        } else {
          this.showError(res.data.message || "Failed to delete poll");
        }
      } catch (err) {
        this.showError("An error occurred");
      } finally {
        this.deleteLoading = false;
        this.pollToDelete = null;
      }
    },

    toggleStatusMenu(e, item) {
      e.preventDefault();
      this.statusMenu.show = false;
      this.statusMenu.x = e.clientX;
      this.statusMenu.y = e.clientY + 10;
      this.statusMenu.item = item;
      this.$nextTick(() => { this.statusMenu.show = true; });
    },

    getAvailableStatuses(item) {
      const statuses = [
        { value: "draft",   label: "Draft" },
        { value: "active",  label: "Active (Publish)" },
        { value: "expired", label: "Expired" },
      ];
      if (!item) return statuses;
      
      const isExpired = item.status === "expired" || new Date(item.expiry_date) < new Date();
      if (isExpired) {
        return statuses.filter(s => s.value !== "active");
      }
      return statuses;
    },

    async updateStatus(newStatus) {
      const poll = this.statusMenu.item;
      this.statusMenu.show = false;
      try {
        const res = await this.$axios.put(`/societyadmin/polls/${poll.id}/status`, { status: newStatus });
        if (res.data.status) {
          poll.status = newStatus;
          this.setAlert({ showAlert: true, message: "Poll status updated successfully", variant: "green accent-4", timeout: 2000 });
        } else {
          this.showError(res.data.message || "Failed to update status");
        }
      } catch (err) {
        this.showError("An error occurred");
      }
    },

    showError(msg) {
      this.setAlert({ showAlert: true, message: msg, variant: "red accent-3", timeout: 2500 });
    },

    formatDate(d) {
      if (!d) return "-";
      return new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit", month: "short", year: "numeric",
      });
    },

    getTypeLabel(type) {
      return { single_choice: "Single Choice", multiple_choice: "Multiple Choice", text: "Text Answer", number: "Number Answer" }[type] || type;
    },

    getTypeIcon(type) {
      return { single_choice: "mdi-radiobox-marked", multiple_choice: "mdi-checkbox-marked-outline", text: "mdi-text-box-outline", number: "mdi-numeric" }[type] || "mdi-help-circle-outline";
    },

    getStatusLabel(status) {
      return status ? status.charAt(0).toUpperCase() + status.slice(1) : "";
    },

    getStatusColor(status) {
      return { draft: "#94A3B8", active: "#10B981", expired: "#F59E0B", deleted: "#EF4444" }[status] || "#94A3B8";
    },

    getStatusStyles(status) {
      const color = this.getStatusColor(status);
      return { background: `${color}15`, color, border: `1px solid ${color}30` };
    },
  },
};
</script>

<style scoped>
.page-wrap {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.cell-name {
  font-size: 14px;
  font-weight: 700;
  color: var(--tx);
}
.date-text {
  font-size: 13px;
  color: #475569;
  font-weight: 500;
}
.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  /* cursor: pointer; */
  gap: 4px;
  transition: filter 0.2s;
}
.status-pill:hover { filter: brightness(0.95); }
.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
</style>
